/*
 * Copyright (c) 2017-2026 Brighter AI Technologies GmbH. All rights reserved.
 * Unauthorized use, copying, reproduction, or distribution of this file, via any medium,
 * is strictly prohibited.
 */

import AccountSettingsIcon from "../assets/AppBar/account-settings.svg";
import LogoutIcon from "../assets/AppBar/logout.svg";
import MenuIcon from "../commons/MenuIcon";
import { planUpdatedAction } from "../reducers/planReducer";
import { OverConsumedBanner } from "./OverConsumedBanner";
import Logo from "./Logo";
import { UserMenu } from "./UserMenu";
import { Button } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { useDataProvider } from "ra-core";
import { forwardRef, useEffect, useState } from "react";
import { AppBar, MenuItemLink, useLogout, useRedirect } from "react-admin";
import { useDispatch, useSelector } from "react-redux";

const ConfigurationMenu = forwardRef(({ onClick }, ref) => {
  const logout = useLogout();
  return (
    <>
      <MenuItemLink
        to="/users/profile"
        primaryText={"Account & Settings"}
        leftIcon={
          <MenuIcon src={AccountSettingsIcon} alt="Account Settings Icon" />
        }
        onClick={onClick}
      />
      <MenuItemLink
        to="/"
        isActive={() => false}
        primaryText={"Logout"}
        leftIcon={<MenuIcon src={LogoutIcon} alt="Logout Icon" />}
        onClick={logout}
      />
    </>
  );
});

const menuStyles = makeStyles((theme) => ({
  userButton: {
    color: "white",
    borderColor: theme.palette.invisible,
  },
}));

const MyUserMenu = (props) => {
  return (
    <div data-hj-suppress>
      <UserMenu label="Profile" classes={menuStyles()} {...props}>
        <ConfigurationMenu />
      </UserMenu>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  title: {
    flex: 1,
    lineHeight: 0,
  },
  appbar: {
    zIndex: 6666,
    "& .MuiIconButton-root": {
      borderColor: theme.palette.invisible,
      "& span": {
        color: "white",
      },
    },
    "& .MuiCircularProgress-root": {
      color: "white",
    },
  },
  emptySpace: {
    marginTop: theme.spacing(3.5),
  },
}));

const subscriptionBadge = makeStyles((theme) => ({
  root: {
    borderColor: theme.palette.primary.main,
  },
  text: {
    color: theme.palette.primary.main,
  },
}));

const MyAppBar = (props) => {
  const classes = useStyles();
  const badgeClasses = subscriptionBadge();
  const dispatch = useDispatch();
  const dataProvider = useDataProvider();
  const userPlan = useSelector((state) => state.userPlan);
  const redirect = useRedirect();
  const [overConsumed, setOverConsumed] = useState(false);

  useEffect(() => {
    async function update() {
      const response = await dataProvider.getOne(`users`, { id: "me" });
      if (typeof response !== "undefined") {
        dispatch(planUpdatedAction(response.data));
        setOverConsumed(response.data.subscriptionStatus === "payed_overconsumed")
      }
    }
    update();
  }, [dataProvider, dispatch]);

  return (
    <>
      <AppBar
        {...props}
        color="secondary"
        elevation={1}
        className={classes.appbar}
        userMenu={<MyUserMenu />}
      >
        <Typography
          variant="h2"
          color="inherit"
          className={classes.title}
          id="react-admin-title"
        >
          <Logo />
        </Typography>
        {userPlan.plan ? (
          <Button
            size="small"
            onClick={() => redirect("/plans")}
            classes={badgeClasses}
          >
            {userPlan.plan}
          </Button>
        ) : null}
      </AppBar>
      <div className={classes.emptySpace} />
      <OverConsumedBanner
        show={overConsumed}
        customTopPadding={userPlan.plan === "trial" || userPlan.plan === "none" ? 0 : undefined}
      />
    </>
  );
};

export default MyAppBar;
